/**
 * CLOCKS - a named count of segments the GM fills.
 *
 * "The patrol returns", "The reactor": so many segments, so many filled.
 * The GM makes one, fills or empties it a segment at a time, and decides
 * who watches it (`audience`, shown.ts). A clock that MARKS TIME stands
 * for the hours themselves: every segment filled on it is one beat of
 * time passing, which is the caller's to run - `beatsOf` says how many.
 *
 * Since v9.5.9 a clock may COUNT DOWN (the same fill, worded as what is
 * left), may fill ONE SEGMENT PER COMBAT ROUND by itself (`perRound`,
 * `tickRound`; such a clock never passes time again - the rounds are the
 * time), and knows when it BECAME FULL (`fullAt`, stamped once on the
 * crossing, cleared when it drops below), which is what lets the full
 * line and the riders fire exactly once across reloads.
 *
 * Every function returns a new clock, or the same one when nothing changed.
 * Pure, zero React dependency.
 */
import { audienceOf, readShownBase } from './shown';
import type { Shown } from './shown';

export const MIN_SEGMENTS = 2;
export const MAX_SEGMENTS = 12;
const DEFAULT_SEGMENTS = 6;
const MAX_NAME = 60;

type ClockDirection = 'up' | 'down';

export interface Clock extends Shown {
  name: string;
  segments: number;
  /** Segments consumed, whichever way the clock counts. */
  filled: number;
  /** Each segment filled on this clock is one beat of time passing. */
  marksTime: boolean;
  /** Counting up to full, or down to nothing: the same fill, worded differently. */
  direction: ClockDirection;
  /** What a full clock does beyond its line: a kept board entry it reveals. */
  riders?: { reveals?: string };
  /** When it became full; null while it is not. */
  fullAt: number | null;
  /** One segment per combat round, on the directing screen. */
  perRound: boolean;
  /** The round last accounted for by `tickRound`; null before the first. */
  roundAt: number | null;
}

const clamp = (n: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, Math.round(n)));
const cleanName = (name: string) => name.trim().slice(0, MAX_NAME) || 'Clock';
const cleanSegments = (n: unknown) =>
  typeof n === 'number' && Number.isFinite(n) ? clamp(n, MIN_SEGMENTS, MAX_SEGMENTS) : DEFAULT_SEGMENTS;

/** A new clock: empty, counting up, the GM's alone until shown. */
export function newClock(args: { id: string; name: string; segments?: number; createdAt: number }): Clock {
  return {
    id: args.id,
    name: cleanName(args.name),
    segments: cleanSegments(args.segments),
    filled: 0,
    audience: [],
    visible: false,
    marksTime: false,
    direction: 'up',
    fullAt: null,
    perRound: false,
    roundAt: null,
    createdAt: args.createdAt,
  };
}

export const isFull = (clock: Pick<Clock, 'filled' | 'segments'>): boolean => clock.filled >= clock.segments;

/** The clock at this fill, its `fullAt` stamped on the crossing to full and cleared on the way back. */
function atFill(clock: Clock, filled: number, now: number): Clock {
  const full = filled >= clock.segments;
  const fullAt = full ? (clock.fullAt ?? now) : null;
  return { ...clock, filled, fullAt };
}

/** Fill (`by` > 0) or empty (`by` < 0) segments, never past either end. */
export function advanceClock(clock: Clock, by: number, now: number): Clock {
  const filled = clamp(clock.filled + by, 0, clock.segments);
  return filled === clock.filled ? clock : atFill(clock, filled, now);
}

export type ClockPatch = Partial<
  Pick<Clock, 'name' | 'segments' | 'visible' | 'audience' | 'marksTime' | 'direction' | 'riders' | 'perRound'>
>;

/**
 * Change what a clock is: its name, its size (the fill is kept, up to the
 * new size - a size cut down to the fill makes it full now), who sees it,
 * how it runs. A rider with nothing in it is dropped.
 */
export function editClock(clock: Clock, patch: ClockPatch, now: number): Clock {
  const segments = patch.segments === undefined ? clock.segments : cleanSegments(patch.segments);
  const { visible: _switch, audience: _list, riders, ...rest } = patch;
  const audience = audienceOf(patch, clock.audience);
  const name = patch.name === undefined ? clock.name : cleanName(patch.name);
  // Switched per-round off or on: whatever round it last counted is no longer its business.
  const roundAt = rest.perRound !== undefined && rest.perRound !== clock.perRound ? null : clock.roundAt;
  const next: Clock = { ...clock, ...rest, name, segments, audience, visible: audience.length > 0, roundAt };
  if (riders !== undefined) {
    if (riders.reveals) next.riders = { reveals: riders.reveals };
    else delete next.riders;
  }
  return atFill(next, Math.min(clock.filled, segments), now);
}

/** Did this change fill the last segment? */
export const becameFull = (before: Clock, after: Clock): boolean => before.fullAt === null && after.fullAt !== null;

/**
 * The round as the directing screen sees it now (null = no order is running).
 * A per-round clock fills ONE segment when the round is after the one it
 * last counted (`roundAt`):
 *
 * - the first round it sees only stamps: a fight's opening round fills nothing;
 * - a later round fills one, HOWEVER FAR it jumped: a jump is not rounds
 *   played (a cut to a scene whose own tracker stands on round 9);
 * - a round not after the stamp (an undo) changes nothing, so the same turn
 *   ended again does not fill twice;
 * - no order running forgets the stamp: the next fight starts from its own.
 *
 * What this costs: a round that turned while no directing screen was open
 * is not caught up, and after a cut to a scene on a LOWER round the clock
 * waits until that scene's round passes the stamp.
 */
export function tickRound(clock: Clock, round: number | null, now: number): Clock {
  if (!clock.perRound) return clock;
  if (round === null) return clock.roundAt === null ? clock : { ...clock, roundAt: null };
  if (clock.roundAt === null) return { ...clock, roundAt: round };
  if (round <= clock.roundAt) return clock;
  return { ...advanceClock(clock, 1, now), roundAt: round };
}

/**
 * How many beats of time a change of this clock stands for: the segments
 * newly filled, when it marks time. None for a per-round clock: the rounds
 * ARE the time, and End Turn already counted the conditions down.
 */
export function beatsOf(before: Clock, after: Clock): number {
  if (!after.marksTime || after.perRound) return 0;
  return Math.max(0, after.filled - before.filled);
}

/** What a FULL clock does beyond its line; nothing while it is not. */
export function clockRiders(clock: Clock): { reveal: string | null } {
  return { reveal: isFull(clock) ? (clock.riders?.reveals ?? null) : null };
}

export const remainingOf = (clock: Clock): number => Math.max(0, clock.segments - clock.filled);

/** "3 / 6", or for a countdown "3 left" and "run out". */
export function countText(clock: Clock): string {
  if (clock.direction !== 'down') return `${clock.filled} / ${clock.segments}`;
  const left = remainingOf(clock);
  return left === 0 ? 'run out' : `${left} left`;
}

/** The log's line when it fills. */
export function fullLine(clock: Clock): string {
  return clock.direction === 'down' ? `The countdown ${clock.name} has run out.` : `The clock ${clock.name} is full.`;
}

const stamp = (raw: unknown): number | null => (typeof raw === 'number' && Number.isFinite(raw) ? raw : null);
const riderOf = (raw: unknown): Clock['riders'] => {
  const reveals = (raw as { reveals?: unknown } | null)?.reveals;
  return typeof reveals === 'string' && reveals ? { reveals } : undefined;
};

/** A clock that crossed the wire, validated; null when it is not one. */
export function readClock(id: string, raw: unknown): Clock | null {
  if (!raw || typeof raw !== 'object') return null;
  const stored = raw as Record<string, unknown>;
  if (typeof stored.name !== 'string') return null;
  const segments = cleanSegments(stored.segments);
  const filled =
    typeof stored.filled === 'number' && Number.isFinite(stored.filled) ? clamp(stored.filled, 0, segments) : 0;
  const riders = riderOf(stored.riders);
  return {
    ...readShownBase(id, stored),
    name: cleanName(stored.name),
    segments,
    filled,
    marksTime: stored.marksTime === true,
    direction: stored.direction === 'down' ? 'down' : 'up',
    ...(riders ? { riders } : {}),
    fullAt: filled >= segments ? (stamp(stored.fullAt) ?? 0) : null,
    perRound: stored.perRound === true,
    roundAt: stamp(stored.roundAt),
  };
}
